import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@lumika/ui";
import { adminApi } from "../../api/admin";

type ChannelTokenPickerProps = {
  /** Which channel type to offer: "web" = website widget, "api" = API. */
  channelType: "web" | "api";
  /** Current token value (owned by the parent view). */
  token: string;
  /** Called both when the user types and when a picked channel resolves. */
  onTokenChange: (token: string) => void;
  tokenLabel: string;
};

// Dealership -> Channel picker that auto-fills an integration token. The
// raw token isn't on the Channel itself, so we read it from the channel's
// onboarding info (adminApi.getChannelOnboarding). The token field stays
// editable so a token can also be pasted by hand.
export function ChannelTokenPicker({
  channelType,
  token,
  onTokenChange,
  tokenLabel,
}: ChannelTokenPickerProps) {
  const [dealershipId, setDealershipId] = useState("");
  const [channelId, setChannelId] = useState("");
  const typeLabel = channelType === "web" ? "widget" : "API";

  const dealershipsQuery = useQuery({
    queryKey: ["dealerships", ""],
    queryFn: () => adminApi.listDealerships(""),
  });
  const channelsQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "channels"],
    queryFn: () => adminApi.listChannels(dealershipId),
    enabled: Boolean(dealershipId),
  });
  const onboardingQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "channel-onboarding", channelId],
    queryFn: () => adminApi.getChannelOnboarding(dealershipId, channelId),
    enabled: Boolean(dealershipId && channelId),
  });

  // Ref so the token-sync effect doesn't depend on the parent's callback
  // identity (which would re-run it every render).
  const onTokenChangeRef = useRef(onTokenChange);
  useEffect(() => {
    onTokenChangeRef.current = onTokenChange;
  }, [onTokenChange]);
  useEffect(() => {
    if (onboardingQuery.data) {
      onTokenChangeRef.current(onboardingQuery.data.token ?? "");
    }
  }, [onboardingQuery.data]);

  const dealerships = dealershipsQuery.data ?? [];
  const channels = (channelsQuery.data?.items ?? []).filter(
    (c) => c.type === channelType,
  );

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <FormField label="Dealership">
        <Select
          value={dealershipId}
          onValueChange={(v) => {
            setDealershipId(v);
            setChannelId("");
          }}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={
                dealershipsQuery.isLoading ? "Loading…" : "Select a dealership"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {dealerships.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      <FormField
        label={`${channelType === "web" ? "Widget" : "API"} channel`}
        description={
          dealershipId && !channelsQuery.isLoading && channels.length === 0
            ? `No ${typeLabel} channels for this dealership.`
            : undefined
        }
      >
        <Select
          value={channelId}
          onValueChange={setChannelId}
          disabled={!dealershipId || channels.length === 0}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={
                !dealershipId
                  ? "Select a dealership first"
                  : channelsQuery.isLoading
                    ? "Loading…"
                    : `Select a ${typeLabel} channel`
              }
            />
          </SelectTrigger>
          <SelectContent>
            {channels.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      <FormField
        label={tokenLabel}
        className="sm:col-span-2"
        description={
          onboardingQuery.isFetching
            ? "Loading token…"
            : "Pick a channel to fill this, or paste a token."
        }
      >
        <Input
          value={token}
          onChange={(e) => onTokenChange(e.target.value)}
          placeholder="Integration token"
          autoComplete="off"
          className="font-mono"
        />
      </FormField>
    </div>
  );
}
